import React from 'react';
import { Box, Container, Typography, Divider, alpha } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';

const NAVY = '#1e3a8a';
const TEAL = '#0d9488';
const SLATE_900 = '#0f172a';
const SLATE_500 = '#64748b';
const SLATE_400 = '#94a3b8';
const SLATE_200 = '#e2e8f0';
const BG = '#f8fafc';

const FAQ = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      question: 'Is the equity split calculated by your platform legally binding?',
      answer: 'The equity split calculations are recommendations from a structured weighted formula based on your questionnaire responses. The generated Founders\' Agreement document is a jurisdiction-aware **starting-point template** (US, India, UK, or Other) — it is NOT a legally binding contract on its own. To make it enforceable, you must (1) have it reviewed and adapted by qualified legal counsel in your jurisdiction, (2) execute it properly under local law, and (3) file it with the relevant authorities where required. The template includes prominent disclaimers and a "Before You Sign" checklist to guide you.',
    },
    {
      question: 'Can I use Guild Space with someone I already know?',
      answer: 'Yes! Guild Space works for both finding new co-founders and formalizing partnerships with people you already know. You can invite an existing co-founder to join your workspace, complete the equity questionnaire together, and use our tools to track KPIs, make decisions, and manage your partnership.',
    },
    {
      question: 'How is my data protected?',
      answer: 'We take data security seriously. All data is encrypted in transit and at rest. We use industry-standard security practices and only share information within your workspace with authorized members. We never sell your personal information. For more details, please review our Privacy Policy.',
    },
    {
      question: 'What happens if we dissolve the partnership?',
      answer: 'The Founders\' Agreement templates generated by Guild Space include provisions for vesting schedules (with cliff and acceleration options) and standard departure clauses (Good Leaver / Bad Leaver, share buy-back rights, voluntary resignation, termination for cause). However, the specific terms that apply to you depend on the agreement you actually execute and the laws of your jurisdiction. We strongly recommend working with a qualified lawyer to adapt these terms to your situation before signing. Your workspace data remains accessible to all members unless you choose to delete it.',
    },
    {
      question: 'How does the matching algorithm work?',
      answer: 'When you want to join a team, you complete a short intent questionnaire (role, stage, industries, work style, and optional priorities). We score projects against that profile and the project\'s compatibility answers—not random feeds. You see a curated set of projects in your daily batch; limits depend on your plan (for example, Free includes up to three curated projects per day and one outbound application per day). Compatibility scores and reasons help you decide where to invest your time before you apply.',
    },
    {
      question: 'Do I need to pay to find a co-founder?',
      answer: 'No. The Free tier includes curated discovery (up to three new projects in your daily batch), one application per day, one project, one workspace, and core workspace tools. Pro adds unlimited applications, more projects and workspaces, visibility into Free and Pro founders\' projects, and access to book paid advisor consultations. Pro+ adds visibility into all founder tiers, 30-day post-match support, and priority check-in support. You only pay when you choose a paid founder plan or when you book a paid advisor session (those fees go to the advisor).',
    },
    {
      question: 'Can I use Guild Space if I already have a co-founder?',
      answer: 'Absolutely. Many teams use Guild Space to structure their existing partnership, formalize equity splits, track KPIs, and maintain accountability. You can skip the discovery phase and go straight to setting up your workspace.',
    },
    {
      question: 'How do advisors work on Guild Space?',
      answer: 'There are two different ideas. Marketplace advisors list a profile, set their own rates for 30- and 60-minute consultations, and share a Cal.com (or similar) booking link. Pro and Pro+ founders can request a session; payment is agreed and sent directly to the advisor (for example UPI, PayPal, or bank transfer)—Guild Space does not take a cut on that payment. After an advisor\'s first confirmed consultation, they get a trial period and then a small Pro Advisor platform subscription so they can keep accepting new bookings. Separately, in a workspace, your equity setup may reserve an advisor equity slice for someone in an advisor role—that is between you and your co-founders and is unrelated to marketplace consultation fees.',
    },
    {
      question: 'What if I disagree with the calculated equity split?',
      answer: 'The equity calculator provides AI-recommended splits based on your inputs, but you\'re not locked into them. You can create custom split scenarios, adjust percentages manually, and discuss different options with your co-founder. The goal is to help you have informed conversations, not to dictate the final split.',
    },
    {
      question: 'Can I export my data?',
      answer: 'Yes, you can download documents (agreements, decisions, etc.) from your workspace at any time. For other data exports, please contact our support team. We believe you should have full control over your information.',
    },
    {
      question: 'What makes Guild Space different from other co-founder matching platforms?',
      answer: 'Guild Space combines curated discovery (intent-first scoring and paced daily batches) with a full workspace: equity workflows and templates, Founder Date milestones for intro calls, workspace chat, first-match coaching prompts, KPIs, decisions, weekly check-ins, and optional Slack and Notion. After you match, you are not dumped into email threads—your partnership has a dedicated home in the product.',
    },
    {
      question: 'Is there a mobile app?',
      answer: 'Guild Space is currently a web application optimized for mobile browsers. You can access it from any device with a modern browser. We\'re working on native mobile apps for iOS and Android—stay tuned!',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: BG, py: { xs: 4, md: 6 } }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 3,
              color: SLATE_500,
              textTransform: 'none',
              '&:hover': { color: TEAL, bgcolor: 'transparent' },
            }}
          >
            Back
          </Button>
          <Typography variant="h3" sx={{ fontWeight: 800, color: SLATE_900, mb: 2, fontSize: { xs: '2rem', md: '2.5rem' } }}>
            Frequently Asked Questions
          </Typography>
          <Typography variant="body1" sx={{ color: SLATE_500, lineHeight: 1.8 }}>
            Find answers to common questions about Guild Space, equity splits, partnerships, and more.
          </Typography>
        </Box>

        {/* FAQ Items */}
        {faqs.map((faq, index) => (
          <Box key={index} sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: SLATE_900, mb: 1.5, fontSize: '1.15rem' }}>
              {faq.question}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: SLATE_500,
                lineHeight: 1.8,
                fontSize: '0.95rem',
              }}
            >
              {faq.answer}
            </Typography>
            {index < faqs.length - 1 && <Divider sx={{ mt: 3, borderColor: SLATE_200 }} />}
          </Box>
        ))}

        {/* Contact note */}
        <Box sx={{
          mt: 6, p: 3, borderRadius: 2,
          bgcolor: alpha(TEAL, 0.05), border: '1px solid', borderColor: alpha(TEAL, 0.2),
        }}>
          <Typography variant="body2" sx={{ color: SLATE_500, lineHeight: 1.8, mb: 1 }}>
            <strong>Still have questions?</strong>
          </Typography>
          <Typography variant="body2" sx={{ color: SLATE_500, lineHeight: 1.8 }}>
            If you can't find the answer you're looking for, feel free to reach out through our feedback system (available when signed in) or contact us directly. We're here to help!
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default FAQ;
